import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { useDebounce } from "use-debounce";
import { Filters } from "../EmployeeList/hook/useFetchArchives";
import { useEmployees } from "../EmployeeList/hook/useFetchEmployees";
import { updateSearchParams } from "src/utils";
import Search from "src/components/input/Search";
import { Trash } from "lucide-react";
import { CustomerListPagination } from "../../ManageCustomers/components/customer-pagination";
import GoalEmployeeList from "./components/employee-list";

const PerformanceManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const pagesize = 10;

  const name = searchParams.get("fullname") || "";
  const employee_code = searchParams.get("employee_code") || "";

  const [debouncedName] = useDebounce(name, 500);
  const [debouncedCode] = useDebounce(employee_code, 500);

  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);

  const filters: Filters = {
    page,
    pagesize,
    name: debouncedName,
    employee_code: debouncedCode,
  };

  const {
    data: employees = [],
    isLoading,
    error,
    isFetching,
  } = useEmployees(filters);

  const hasNextPage = employees?.length === pagesize;
  const hasPrevPage = page > 1;

  useEffect(() => {
    if (!employees) return;
    if (employees?.length > 0) {
      setFailSafePageNumber(page);
    } else if (failSafePageNumber !== page) {
      toast.info("No Page!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  const handleClear = () => {
    setSearchParams();
  };

  return (
    <div className="pt-4">
      {/* Header */}
      <HRTitleHeader
        title="Performance Management"
        info="Goals, reviews and appraisal cycles"
      />
      <div className="flex justify-between mb-4 sticky top-0 backdrop-blur-lg">
        <div className="flex gap-4">
          <Search
            value={name}
            placeholder="Search by Name"
            onChange={(value) =>
              updateSearchParams(
                "fullname",
                value,
                setSearchParams,
                searchParams,
              )
            }
          />
          <Search
            value={employee_code}
            placeholder="Search by Employee Code"
            onChange={(value) =>
              updateSearchParams(
                "employee_code",
                value,
                setSearchParams,
                searchParams,
              )
            }
          />
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 h-10 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:text-red-600 transition"
          >
            <Trash />
          </button>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-card rounded-xl shadow-lg p-6">
        {isLoading && (
          <p className="text-gray-500">
            Loading employees...{" "}
            <span className="loading loading-dots loading-sm"></span>
          </p>
        )}
        {error && <p className="text-red-500">{error.message}</p>}
        {!isLoading && !error && employees.length === 0 && (
          <p className="text-gray-500">No employees found.</p>
        )}
        <GoalEmployeeList employees={employees} />
      </div>
      <div className="flex-grow my-3">
        <CustomerListPagination
          currentPageNumber={page}
          onNext={handleNextPage}
          onPrev={handlePrevPage}
          onJumpToPage={handleJumpToPage}
          isFetching={isFetching}
          hasPrev={hasPrevPage}
          hasNext={hasNextPage}
        />
      </div>
    </div>
  );
};

export default PerformanceManagement;
